import { useEffect, useRef } from 'react';
import L from 'leaflet';

export default function MapView({
  lines,
  currentPoints,
  currentColor,
  currentWeight,
  isDrawing,
  onMapClick,
  onMapReady,
}) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const linesLayerRef = useRef(null);
  const currentLineRef = useRef(null);
  const markersLayerRef = useRef(null);
  const clickCbRef = useRef(onMapClick);

  useEffect(() => {
    clickCbRef.current = onMapClick;
  }, [onMapClick]);

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = L.map(mapContainerRef.current, {
      center: [12.8, 121.8],
      zoom: 6,
      zoomControl: false,
      attributionControl: false,
    });

    L.control.zoom({ position: 'bottomright' }).addTo(map);

    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      crossOrigin: true,
    }).addTo(map);

    linesLayerRef.current = L.layerGroup().addTo(map);
    markersLayerRef.current = L.layerGroup().addTo(map);

    map.on('click', (e) => {
      if (clickCbRef.current) clickCbRef.current(e.latlng);
    });

    mapRef.current = map;
    // Leaflet needs a size recalculation when mounted inside a flex container.
    const resize = () => map.invalidateSize();
    const t = setTimeout(resize, 200);
    window.addEventListener('resize', resize);

    onMapReady(map);

    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', resize);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Cursor style
  useEffect(() => {
    if (!mapContainerRef.current) return;
    const container = mapContainerRef.current;
    if (isDrawing) {
      container.classList.add('drawing-mode');
      container.classList.remove('default-mode');
    } else {
      container.classList.remove('drawing-mode');
      container.classList.add('default-mode');
    }
  }, [isDrawing]);

  // Render saved lines
  useEffect(() => {
    if (!linesLayerRef.current || !markersLayerRef.current) return;

    linesLayerRef.current.clearLayers();
    markersLayerRef.current.clearLayers();

    lines.forEach((line) => {
      if (!line.visible || line.points.length < 2) return;

      const polyline = L.polyline(line.points, {
        color: line.color,
        weight: line.weight,
        opacity: 0.85,
        smoothFactor: 1,
      });
      linesLayerRef.current.addLayer(polyline);

      if (line.tag) {
        const midIndex = Math.floor(line.points.length / 2);
        const midPoint = line.points[midIndex];
        const tooltip = L.tooltip({
          permanent: true,
          direction: 'top',
          className: 'line-tag-tooltip',
          offset: [0, -10],
        })
          .setLatLng(midPoint)
          .setContent(line.tag);
        markersLayerRef.current.addLayer(tooltip);
      }
    });
  }, [lines]);

  // Render the in-progress drawing line
  useEffect(() => {
    if (!mapRef.current) return;

    if (currentLineRef.current) {
      mapRef.current.removeLayer(currentLineRef.current);
      currentLineRef.current = null;
    }

    if (currentPoints.length >= 1) {
      currentLineRef.current = L.polyline(currentPoints, {
        color: currentColor,
        weight: currentWeight,
        opacity: 0.9,
        dashArray: '8, 8',
        smoothFactor: 1,
      }).addTo(mapRef.current);

      currentPoints.forEach((point) => {
        const [lat, lng] = point;
        L.circleMarker([lat, lng], {
          radius: 4,
          color: currentColor,
          fillColor: '#ffffff',
          fillOpacity: 1,
          weight: 2,
        }).addTo(linesLayerRef.current);
      });
    }
  }, [currentPoints, currentColor, currentWeight]);

  return (
    <div
      ref={mapContainerRef}
      data-testid="mappoint-map"
      className="w-full h-full"
      style={{ background: '#e8edf5' }}
    />
  );
}
